import { HttpErrorResponse } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { Component, effect, HostListener, inject, Input, PLATFORM_ID, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { finalize, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AcquisitionCodeService } from '../../core/services/acquisition-code.service';
import { AuthService } from '../../core/services/auth.service';
import type { CartItem } from '../cart/cart.model';
import { CartService } from '../cart/cart.service';
import { CheckoutService } from '../checkout/checkout.service';
import { ProductReviewsService } from '../products/product-reviews.service';
import { MediaImageComponent } from '../../shared/components/media-image/media-image.component';

const QTY_PRESETS = [1, 2, 3, 4, 6, 12];

@Component({
  selector: 'app-bag',
  standalone: true,
  imports: [RouterLink, MediaImageComponent],
  templateUrl: './bag.component.html',
  styleUrl: './bag.component.scss',
})
export class BagComponent {
  private readonly reviewsApi = inject(ProductReviewsService);
  private readonly checkoutService = inject(CheckoutService);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  readonly cartService = inject(CartService);
  readonly authService = inject(AuthService);
  private readonly acquisitionCodes = inject(AcquisitionCodeService);

  @Input() variant: 'page' | 'drawer' = 'page';

  readonly detailsOpen = signal(false);
  readonly couponOpen = signal(false);
  readonly couponError = signal('');
  readonly isCouponLoading = signal(false);
  readonly openQtyKey = signal<string | null>(null);
  readonly reviewCounts = signal<Record<number, number>>({});

  constructor() {
    effect(() => {
      const catalogIds = [...new Set(this.cartService.items().map((item) => item.catalogId))];
      this.loadReviews(catalogIds);
    });
  }

  reviewCount(catalogId: number): number {
    return this.reviewCounts()[catalogId] ?? 0;
  }

  qtyOptions(current: number): number[] {
    const options = new Set(QTY_PRESETS);
    options.add(current);
    return [...options].sort((a, b) => a - b);
  }

  toggleQty(event: Event, item: CartItem): void {
    event.preventDefault();
    event.stopPropagation();
    const key = this.cartService.lineKey(item.catalogId, item.variantId);
    this.openQtyKey.update((open) => (open === key ? null : key));
  }

  setQuantity(item: CartItem, quantity: number): void {
    this.cartService.updateQuantity(item.catalogId, item.variantId, quantity);
    this.openQtyKey.set(null);
  }

  removeItem(item: CartItem): void {
    this.cartService.removeItem(item.catalogId, item.variantId);
  }

  toggleCoupons(): void {
    this.couponOpen.update((open) => !open);
  }

  onCouponInput(value: string): void {
    this.cartService.setPendingCouponCode(value);
    if (this.couponError()) {
      this.couponError.set('');
    }
  }

  applyCoupon(): void {
    const code = this.cartService.pendingCouponCode().trim();
    this.couponError.set('');

    if (!code) {
      this.couponError.set('Enter a coupon code.');
      return;
    }

    if (!this.authService.isAuthenticated()) {
      this.couponError.set('Please sign in to apply a coupon.');
      this.cartService.setPendingCouponCode(code);
      return;
    }

    if (this.cartService.items().length === 0) {
      this.couponError.set('Add items to your bag before applying a coupon.');
      return;
    }

    this.isCouponLoading.set(true);
    this.checkoutService
      .previewCoupon(code, this.cartService.subtotal())
      .pipe(finalize(() => this.isCouponLoading.set(false)))
      .subscribe({
        next: (result) => {
          this.cartService.setAppliedCoupon({
            code: result.couponCode,
            discountAmount: result.discountAmount,
          });
          this.couponError.set('');
        },
        error: (err: HttpErrorResponse) => {
          this.cartService.setAppliedCoupon(null);
          this.cartService.setPendingCouponCode(code);
          this.couponError.set(this.readCouponError(err));
        },
      });
  }

  clearCoupon(): void {
    this.cartService.setAppliedCoupon(null);
    this.couponError.set('');
  }

  goBack(event: Event): void {
    event.preventDefault();
    if (this.variant === 'drawer') {
      this.cartService.closeDrawer();
      return;
    }
    if (isPlatformBrowser(this.platformId) && window.history.length > 1) {
      window.history.back();
      return;
    }
    void this.router.navigate(['/all-products']);
  }

  onNavigate(): void {
    if (this.variant === 'drawer') {
      this.cartService.closeDrawer();
    }
  }

  authOfferParams(): Record<string, string> {
    return this.acquisitionCodes.buildAuthNavigationQueryParams('/bag');
  }

  viewBag(): void {
    this.cartService.closeDrawer();
    void this.router.navigate(['/bag']);
  }

  proceed(): void {
    if (this.cartService.isNavigatingToCheckout() || this.cartService.items().length === 0) {
      return;
    }

    this.cartService.goToCheckout();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Element | null;
    if (!target?.closest('.bag-product .dropdown')) {
      this.openQtyKey.set(null);
    }
  }

  private readCouponError(err: HttpErrorResponse): string {
    const body = err.error as { message?: string; title?: string; detail?: string } | string | null;
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    if (body && typeof body === 'object') {
      return body.message ?? body.detail ?? body.title ?? 'Coupon code is invalid.';
    }
    return 'Coupon code is invalid.';
  }

  private loadReviews(catalogIds: number[]): void {
    if (catalogIds.length === 0) {
      return;
    }

    const known = this.reviewCounts();
    const missing = catalogIds.filter((id) => known[id] == null);
    if (missing.length === 0) {
      return;
    }

    forkJoin(
      missing.map((catalogId) =>
        this.reviewsApi.getProductReviews(catalogId, 1, 1).pipe(
          catchError(() => of({ reviewCount: 0 })),
        ),
      ),
    ).subscribe((pages) => {
      const next = { ...this.reviewCounts() };
      missing.forEach((catalogId, index) => {
        next[catalogId] = pages[index].reviewCount ?? 0;
      });
      this.reviewCounts.set(next);
    });
  }
}
